import { API_ENDPOINTS } from '../config/api'
import { safeJson } from '../lib/utils'
import type { TopFailedControl, TopOpenRisk, TopVulnerability } from './informationSystemOperationalApi'

type ApiResult<T> = { success: boolean; data?: T; message?: string }

async function apiFetch<T>(url: string, token: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(init?.headers || {}),
    },
  })
  const body = (await safeJson(res)) as ApiResult<T> & { message?: string }
  if (!res.ok) throw new Error(body?.message || `Request failed (${res.status})`)
  if (body && typeof body === 'object' && 'success' in body && body.success === false) {
    throw new Error(body.message || 'Request failed')
  }
  return (body?.data ?? body) as T
}

function orgQuery(orgId?: string | null) {
  const p = new URLSearchParams()
  if (orgId) p.set('organization_id', orgId)
  const s = p.toString()
  return s ? `?${s}` : ''
}

export type LinkedSystemRef = {
  id: string
  name: string
  code?: string | null
  lifecycleStatus?: string | null
  criticality?: string | null
  relationshipType?: string | null
}

export type AssetOperationalSummaryRow = {
  assetId: number | string
  name: string
  type: string
  environment: string | null
  criticality: string | null
  ownerLabel?: string | null
  networkTarget?: string | null
  linkedSystemsCount: number
  dataCategories: string[]
  vulnerabilityCount: number
  criticalVulnerabilityCount: number
  failedControlsCount: number
  openRisksCount: number
  evidenceGapCount?: number
  remediationAgingCount?: number
  exposureIndicators?: string[]
  topVulnerabilities: TopVulnerability[]
  topFailedControls: TopFailedControl[]
  topOpenRisks: TopOpenRisk[]
  lastScan?: string | null
}

export type AssetOperationalDetail = AssetOperationalSummaryRow & {
  riskLevel?: string | null
  riskScore?: number | null
  compliancePercent?: number | null
  linkedSystems: LinkedSystemRef[]
  dataCategoryDetails?: Array<{ category: string; classification?: string | null; systemId: string }>
  remediationOpenCount?: number
  scores?: { riskScore: number | null; complianceScore: number | null; note?: string }
}

export type GraphNode = {
  id: string
  type: string
  label: string
  status?: string | null
  metadata?: Record<string, unknown>
}

export type GraphEdge = {
  id: string
  source: string
  target: string
  type: string
  label?: string | null
  metadata?: Record<string, unknown>
}

export type AssetGraphData = {
  nodes: GraphNode[]
  edges: GraphEdge[]
  summary: { nodeCount: number; edgeCount: number; layers?: Record<string, boolean>; rootAssetId?: number }
}

export type AssetTimelineEvent = {
  id: string
  kind: string
  title: string
  at: string
  severity?: string | null
  metadata?: Record<string, unknown>
}

export type AssetTimelineData = {
  assetId: number | string
  assetName: string
  events: AssetTimelineEvent[]
}

export async function fetchAssetOperationalSummaries(token: string, orgId?: string | null) {
  return apiFetch<AssetOperationalSummaryRow[]>(
    `${API_ENDPOINTS.ASSETS_OPERATIONAL_SUMMARIES}${orgQuery(orgId)}`,
    token
  )
}

export async function fetchAssetOperationalSummary(
  token: string,
  assetId: string | number,
  orgId?: string | null
) {
  return apiFetch<AssetOperationalDetail>(
    `${API_ENDPOINTS.ASSET_OPERATIONAL_SUMMARY(assetId)}${orgQuery(orgId)}`,
    token
  )
}

export async function fetchAssetGraph(
  token: string,
  assetId: string | number,
  orgId?: string | null,
  layers?: string
) {
  const p = new URLSearchParams()
  if (orgId) p.set('organization_id', orgId)
  if (layers) p.set('layers', layers)
  const q = p.toString()
  return apiFetch<AssetGraphData>(
    `${API_ENDPOINTS.ASSET_GRAPH(assetId)}${q ? `?${q}` : ''}`,
    token
  )
}

export async function fetchAssetTimeline(
  token: string,
  assetId: string | number,
  orgId?: string | null
) {
  return apiFetch<AssetTimelineData>(
    `${API_ENDPOINTS.ASSET_TIMELINE(assetId)}${orgQuery(orgId)}`,
    token
  )
}
